import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Seo } from '@/components/generals/seo'
import { useClinic } from '@/hooks/use-clinic'
import { PageHeader, PageSkeleton, SegmentedControl } from '@/components/app/common'
import { ClinicSettingsPanel } from '@/components/settings/clinic-settings'
import { PrescriptionSettingsPanel } from '@/components/settings/prescription-settings'
import { MessageSettingsPanel } from '@/components/settings/message-settings'
import { TreatmentSettingsPanel } from '@/components/settings/treatment-settings'
import { DataSettingsPanel } from '@/components/settings/data-settings'

type SettingsTab = 'clinic' | 'prescription' | 'messages' | 'treatments' | 'data'

const TABS: Array<{ value: SettingsTab, label: string }> = [
    { value: 'clinic', label: 'Clinic' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'messages', label: 'Follow-ups and WhatsApp' },
    { value: 'treatments', label: 'Treatments' },
    { value: 'data', label: 'Data' }
]

const isTab = (value: unknown): value is SettingsTab => TABS.some(item => item.value === value)

const SettingsPage: FC = (): ReactNode => {
    const router = useRouter()
    const { ready } = useClinic()
    const [tab, setTab] = useState<SettingsTab>('clinic')

    useEffect(() => {
        if (!router.isReady) return

        if (isTab(router.query.tab)) setTab(router.query.tab)
    }, [router.isReady, router.query.tab])

    if (!ready) {
        return (
            <>
                <Seo title='Settings' />
                <PageSkeleton />
            </>
        )
    }

    const chooseTab = (value: SettingsTab): void => {
        setTab(value)
        void router.replace({ pathname: '/settings', query: { tab: value } }, undefined, { shallow: true, scroll: false })
    }

    return (
        <>
            <Seo title='Settings' />

            <PageHeader
                title='Settings'
                description='Clinic details, prescription printing, WhatsApp messages and the treatment list.'
            />

            <SegmentedControl label='Settings section' value={tab} onValueChange={chooseTab} options={TABS} className='mb-6' />

            {tab === 'clinic' && <ClinicSettingsPanel />}
            {tab === 'prescription' && <PrescriptionSettingsPanel />}
            {tab === 'messages' && <MessageSettingsPanel />}
            {tab === 'treatments' && <TreatmentSettingsPanel />}
            {tab === 'data' && <DataSettingsPanel />}
        </>
    )
}

export default SettingsPage
